import React, { useEffect, useRef, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';

// Palette estilo Cmd+K. Fontes são passadas como `commands: [{ id, label,
// hint?, icon?, onRun, keywords? }]`. Filtro client-side por `label + keywords`.
// Em vez de fetch dinâmico (pra não complicar), use o prop `extraFetcher`
// opcional pra buscar resultados async quando houver query.
const CommandPalette = ({
  isOpen,
  onClose,
  commands = [],
  extraFetcher,
  placeholder = 'Busque por comandos, lojas, pedidos…',
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [fetched, setFetched] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setFetched([]);
      setActiveIdx(0);
      return;
    }
    inputRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!extraFetcher || !query.trim()) {
      setFetched([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        const results = await extraFetcher(query);
        setFetched(Array.isArray(results) ? results : []);
      } catch {
        setFetched([]);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [query, extraFetcher]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) => {
      const hay = `${c.label} ${c.keywords || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [commands, query]);

  const items = [...filtered, ...fetched];

  useEffect(() => {
    setActiveIdx(0);
  }, [query]);

  const run = (item) => {
    onClose?.();
    if (item.to) navigate(item.to);
    else item.onRun?.();
  };

  const handleKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(items.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (items[activeIdx]) run(items[activeIdx]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose?.();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center pt-[15vh] p-4 bg-black/40"
      onClick={onClose}
      aria-modal="true"
      role="dialog"
      aria-label="Paleta de comandos"
    >
      <div
        className="w-full max-w-xl bg-white rounded-lg shadow-2xl border border-app-border overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 p-3 border-b border-app-border">
          <Search size={18} className="text-app-text-secondary" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKey}
            placeholder={placeholder}
            className="flex-1 text-sm bg-transparent border-none focus:outline-none text-app-text placeholder:text-app-text-secondary"
          />
          <kbd className="text-xs text-app-text-secondary border border-app-border rounded px-1.5 py-0.5 font-mono">
            Esc
          </kbd>
        </div>

        <div className="max-h-[50vh] overflow-y-auto">
          {items.length === 0 ? (
            <p className="p-6 text-center text-sm text-app-text-secondary">Nada encontrado.</p>
          ) : (
            <ul className="py-1">
              {items.map((item, idx) => (
                <li key={item.id || `${item.label}-${idx}`}>
                  <button
                    type="button"
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => run(item)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
                      idx === activeIdx ? 'bg-primary/10 text-primary' : 'text-app-text'
                    }`}
                  >
                    {item.icon && <span className="text-app-text-secondary">{item.icon}</span>}
                    <span className="flex-1 truncate">{item.label}</span>
                    {item.hint && (
                      <span className="text-xs text-app-text-secondary">{item.hint}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CommandPalette;
